import { useState, useCallback, useEffect, useRef } from 'react';
import { subDays, addDays, isSameDay } from 'date-fns';

interface DayNavigationOptions {
    initialDate: Date;
    daysPerPage: number;
    selectedDate?: Date | null;
}

export function useDayNavigation({
    initialDate,
    daysPerPage,
    selectedDate
}: DayNavigationOptions) {
    // Estado para los días actuales y la dirección de desplazamiento
    const [days, setDays] = useState<Date[]>([]);
    const [isScrolling, setIsScrolling] = useState<false | 'prev' | 'next'>(false);

    // Referencia para evitar actualizaciones durante animaciones
    const isAnimatingRef = useRef(false);
    const selectedDateRef = useRef<Date | null>(selectedDate || null);

    // Función para generar días basados en una fecha central
    const generateDays = useCallback((centerDate: Date) => {
        const halfDays = Math.floor(daysPerPage / 2);
        const firstDay = subDays(centerDate, halfDays);
        return Array.from({ length: daysPerPage }, (_, i) => addDays(firstDay, i));
    }, [daysPerPage]);

    // Inicializar días al montar o cuando cambia daysPerPage
    useEffect(() => {
        setDays(generateDays(initialDate));
    }, [generateDays, initialDate]);

    // Actualizar referencia cuando cambia el día seleccionado
    useEffect(() => {
        if (selectedDate) {
            selectedDateRef.current = selectedDate;
        }
    }, [selectedDate]);

    // Cargar días anteriores
    const loadPreviousDays = useCallback(() => {
        if (isAnimatingRef.current || days.length === 0) return;

        isAnimatingRef.current = true;
        setIsScrolling('prev');

        // Calcular nuevos días (moverse hacia atrás en el tiempo)
        const firstCurrentDay = days[0];
        const targetDay = subDays(firstCurrentDay, daysPerPage);

        // Dar tiempo para que comience la animación
        setTimeout(() => {
            setDays(generateDays(targetDay));

            // Liberar bloqueo después de que la animación termine
            setTimeout(() => {
                setIsScrolling(false);
                isAnimatingRef.current = false;
            }, 500);
        }, 50);
    }, [days, daysPerPage, generateDays]);

    // Cargar días siguientes
    const loadNextDays = useCallback(() => {
        if (isAnimatingRef.current || days.length === 0) return;

        isAnimatingRef.current = true;
        setIsScrolling('next');

        // Calcular nuevos días (moverse hacia adelante en el tiempo)
        const lastCurrentDay = days[days.length - 1];
        const targetDay = addDays(lastCurrentDay, 1);

        setTimeout(() => {
            setDays(generateDays(targetDay));

            setTimeout(() => {
                setIsScrolling(false);
                isAnimatingRef.current = false;
            }, 500);
        }, 50);
    }, [days, daysPerPage, generateDays]);

    // Función para establecer una fecha personalizada
    const setCustomStartDate = useCallback((date: Date) => {
        if (isAnimatingRef.current) return;

        // Comprobar si la fecha ya está en la vista actual
        const isDateInView = days.some(day => isSameDay(day, date));
        if (isDateInView) return;

        isAnimatingRef.current = true;

        // Determinar dirección basada en la relación con la vista actual
        let direction: 'prev' | 'next' = 'next';
        if (days.length > 0) {
            direction = date < days[0] ? 'prev' : 'next';
        }

        setIsScrolling(direction);

        setTimeout(() => {
            setDays(generateDays(date));

            setTimeout(() => {
                setIsScrolling(false);
                isAnimatingRef.current = false;
            }, 500);
        }, 50);
    }, [days, generateDays]);

    // Comprobar si el día seleccionado está en vista
    useEffect(() => {
        if (selectedDate && days.length > 0 && !isAnimatingRef.current) {
            const isSelectedDateInView = days.some(day => isSameDay(day, selectedDate));

            if (!isSelectedDateInView) {
                setCustomStartDate(selectedDate);
            }
        }
    }, [selectedDate, days, setCustomStartDate]);

    return {
        days,
        isScrolling,
        loadPreviousDays,
        loadNextDays,
        setCustomStartDate
    };
}